import { AbstractFieldGroupView } from '../base/AbstractFieldGroupView';
import { DateInputView } from '../../../../../renderApp/components/cells/BaseInput/views/DateInputView';
import { BaseInputFactory } from '../../../../../renderApp/components/cells/BaseInput/BaseInputFactory';
import { html, type Html, View } from 'rune-ts';
import {
  ButtonCheckbox,
  InputCheckboxChangeEvent,
} from '../../../../../renderApp/components/atoms/ButtonCheckbox/ButtonCheckbox';
import { font } from '../../../../../shared/typography';
import klass from './SaleStartDateInputField.module.scss';
import input_klass from '../../../../../renderApp/components/cells/DateInputField/styles/BaseInputView.module.scss';
import { head, isUndefined } from '@fxts/core';
import { onCalendarChangeEvent } from '../../../../../renderApp/components/cells/DateInputField/DateInputCalendarController';
import { IValidation, ProductEditorError } from '../types/type';
import { isNessApp } from '../../../../../../../modules/Util/S/Function/service_app_name';
import { ProductBadgeSize } from '@services/MarppleShop/features/ProductBadge';
import { ProductBadgeView } from '@services/MarppleShop/renderApp/components/cells/ProductBadge';

export class SaleStartDateInputField extends AbstractFieldGroupView<Date | undefined> implements IValidation {
  private readonly inputView: DateInputView;
  private readonly disableControlInput: ButtonCheckbox;
  private readonly id = 'start_date';

  private error_message?: string;

  private readonly StaticUpcomingBadge: View | null;

  constructor({ value }: { value: Date | undefined }) {
    super(value, '판매 시작일');
    this.required = !!value;
    this.inputView = BaseInputFactory.create(
      'date',
      value,
      {
        name: this.id,
        format: 'yyyy-MM-dd HH:mm:ss',
        placeholder: '시작일 선택',
        disabled: !value,
      },
      {
        klass: input_klass.input,
      },
    );

    this.disableControlInput = new ButtonCheckbox({
      name: `${this.id}_control`,
      label: '판매 시작일을 설정할게요',
      font_klass: font('14_regular'),
      active: !!value,
      klass: klass.checkbox,
    });

    this.StaticUpcomingBadge = isNessApp()
      ? null
      : new ProductBadgeView(
          {
            type: 'Upcoming',
            startDate: new Date('2999-12-31'),
            options: {
              size: ProductBadgeSize.small,
              lang: ET.lang,
              klass: klass.badge,
              baseTimestamp: Date.now()
            }
          },
        );
  }

  protected body(): Html | View {
    const footer_text = html`설정한 시작일 전에 상품은 ${this.StaticUpcomingBadge ?? '출시 예정'}으로
    표시되며, 시작일이 되면 자동으로 판매가 시작됩니다.`;

    return html`<div class="${klass.sale_date_field}">
      ${this.inputView}
      <span class="${klass.error_message} ${this.isErrorMessageVisible() ? '' : klass.hidden}">
        ${this.error_message}
      </span>
      ${this.disableControlInput}
      <span class="${klass.footer_text} ${font('13_regular')}">${footer_text}</span>
    </div>`;
  }

  public setErrorMessage(message?: string): this {
    this.error_message = message;
    this.syncErrorMessage();
    return this;
  }

  private isErrorMessageVisible(): boolean {
    return !!this.error_message;
  }

  public getValue(): Date | undefined {
    return this.inputView.getValue();
  }

  public clear(): this {
    this.inputView.setValueAndSync(undefined);
    return this;
  }

  private syncErrorMessage() {
    const error_el = this.element().querySelector(`.${klass.error_message}`);
    if (error_el) {
      (error_el as HTMLElement).innerText = this.error_message || '';
      this.inputView.setError(this.isErrorMessageVisible());
      if (this.isErrorMessageVisible()) {
        error_el.classList.remove(klass.hidden);
      } else {
        error_el.classList.add(klass.hidden);
      }
    }
  }

  protected override onRender() {
    this.delegate(InputCheckboxChangeEvent, ButtonCheckbox, (e) => {
      const selected = e.detail;

      this.setRequiredAndSync(selected);
      this.inputView.setAttribute({ disabled: !selected, required: this.isRequired() });
      if (!selected) {
        this.inputView.setValueAndSync(undefined);
        this.showValidation();
      }
      this.inputView.redraw();
    });

    this.delegate(onCalendarChangeEvent, DateInputView, () => {
      this.showValidation();
    });
  }

  public validate(): ProductEditorError[] {
    const errors = [];

    if (this.isRequired() && isUndefined(this.inputView.getValue())) {
      const err = {
        target: this,
        title: '판매 시작일 미선택',
        message: '시작일을 선택해 주세요.',
        detail: '판매 시작일이 설정되지 않았어요. 판매 시작일을 선택해야 등록할 수 있어요.',
      };
      errors.push(err);
    }
    return errors;
  }

  public showValidation() {
    const first_error = head(this.validate());
    this.setErrorMessage(first_error?.message);
    return this;
  }
}
